<template>
  <div class="sidebar" :class="{ 'is-collapsed': collapsed && !mobile, 'is-mobile': mobile, 'is-open': mobile && mobileMenuOpen }">
    <div class="sidebar-header" @click="goHome" :title="collapsed ? brandName + ' — home' : 'Home'">
      <div class="logo-wrap" v-if="!collapsed || mobile">
        <BrandMark :fill="logoFill" :surface="logoSurface" />
      </div>
      <span class="brand-mark" v-else>{{ brandInitials }}</span>
      <div class="brand-identity" v-if="!collapsed || mobile">
        <span class="brand-tag">Connect Panel</span>
      </div>
      <button v-if="mobile" type="button" class="sidebar-close" title="Close" @click.stop="$store.dispatch('closeMobileMenu')">✕</button>
    </div>

    <DashboardMenu ref="menu" :compact="collapsed && !mobile" @expand="expand"></DashboardMenu>
  </div>
</template>

<script>
import { mapState } from "vuex";
import DashboardMenu from "@/components/Dashboard/Sidebar/Menu/Menu";
import brand from "@/brand";

/**
 * The left navigation. Full (260 px, labels and groups) or a rail (64 px, icons
 * with tooltips); the choice is remembered per browser. The store picker and
 * the theme switch live in the top bar now.
 */
export default {
  name: "Sidebar",
  components: { DashboardMenu },
  computed: {
    ...mapState({
      stateCommon: "common",
      collapsed: (s) => s.panel.sidebarCollapsed,
      mobile: (s) => s.panel.mobile,
      mobileMenuOpen: (s) => s.panel.mobileMenuOpen,
    }),
    // The sidebar's own background where the brand fixes it (brand.js sidebarSurface), else the theme's.
    logoSurface() {
      return brand.sidebarSurface || null;
    },
    logoFill() {
      return (this.logoSurface || this.stateCommon.theme) === "dark" ? "#e2e8f0" : "#0f172a";
    },
    brandName() { return brand.name; },
    brandInitials() {
      return String(brand.name || "C").split(/\s+/).map((w) => w.charAt(0)).join("").slice(0, 2).toUpperCase();
    },
  },
  methods: {
    goHome() {
      if (this.$route.path === "/") return;
      this.$router.push("/").catch(() => {});
    },
    expand() {
      this.$store.commit("SET_SIDEBAR_COLLAPSED", false);
    },
  },
};
</script>

<style lang="css" scoped>
.sidebar {
  background: var(--bg-sidebar);
  position: fixed;
  left: 0;
  top: 0;
  z-index: 26;
  width: var(--sidebar-width, 260px);
  height: 100vh;
  display: flex;
  flex-direction: column;
  border-right: 1px solid var(--sidebar-border);
  overflow: hidden;
  transition: width var(--motion-base) var(--ease-out), transform var(--motion-base) var(--ease-out);
}
/* Phone: the menu floats over the page and is closed until ☰ is tapped. */
.sidebar.is-mobile {
  width: min(84vw, 300px);
  z-index: 40;
  transform: translateX(-100%);
  box-shadow: none;
}
.sidebar.is-mobile.is-open {
  transform: translateX(0);
  box-shadow: 0 0 0 100vmax rgba(0, 0, 0, 0.45), var(--shadow-overlay);
}
.sidebar-close {
  margin-left: auto;
  width: 32px;
  height: 32px;
  border: 0;
  border-radius: var(--r-md);
  background: transparent;
  color: var(--text-muted);
  font-size: var(--t-body);
  cursor: pointer;
}
.sidebar-header {
  height: var(--topbar-height);
  padding: 0 1.1rem;
  border-bottom: 1px solid var(--sidebar-border);
  flex-shrink: 0;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  transition: background var(--motion-fast) var(--ease-out);
}
.is-collapsed .sidebar-header {
  justify-content: center;
  padding: 0;
}
.sidebar-header:hover {
  background: var(--bg-hover);
}
.logo-wrap {
  width: 96px;
  flex-shrink: 0;
}
/* The brand mark may be a vector or a bitmap; the brands' marks are all wide
   wordmarks of roughly the same proportion, so one rule covers them. */
.logo-wrap svg,
.logo-wrap img {
  display: block;
  width: 100%;
  height: auto;
}
.brand-mark {
  width: 32px;
  height: 32px;
  border-radius: 9px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: var(--gradient-accent);
  color: #fff;
  font-size: 12px;
  font-weight: 800;
  letter-spacing: 0.04em;
}
.brand-identity {
  display: flex;
  flex-direction: column;
  gap: 0.22rem;
  min-width: 0;
  flex: 1;
}
.brand-tag {
  font-size: var(--t-caption);
  line-height: var(--t-caption-lh);
  font-weight: 500;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--brand-orange, #f97316);
  opacity: 0.85;
  white-space: nowrap;
}
</style>
